import * as AK from '@ariakit/react'
import classNames from 'classnames'
import {getIn} from 'final-form'
import * as R from 'rambdax'
import {
  Children,
  type ComponentProps,
  type FC,
  Fragment,
  type ReactElement,
  type ReactNode,
  type Ref,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as Final from 'react-final-form'
import * as Listen from 'react-final-form-listeners'
import {
  FiArrowRight,
  FiBook,
  FiCheck,
  FiChevronDown,
  FiCpu,
  FiExternalLink,
  FiLink,
  FiMenu,
  FiMoreHorizontal,
  FiMoreVertical,
  FiPlus,
  FiPlusSquare,
  FiTrash,
  FiUser,
} from 'react-icons/fi'
import {RxDragHandleDots2} from 'react-icons/rx'
import {
  type Falsish,
  IS_DEV,
  IS_STORYBOOK,
  IS_TEST,
  dropWhile,
  ensureArray,
  logger,
  notNullish,
  semiJoin,
  takeWhile,
  truthy,
} from 'tizra'
import {useAdminContext, useReskin} from './hooks'
import {notSystemProp} from 'quickstart/styled-components/system'
import * as S from './styles'
import {compassDirections, justifications} from 'quickstart/types'

const log = logger('blocks/admin')

interface AdminWindow extends Window {
  hideDialog: (el: HTMLElement) => void
  navOut: (code: string, s: 'eval') => void
}

declare let window: AdminWindow

/**
 * Add data-testid in storybook and under test.
 */
export const tid = (...ids: unknown[]) => {
  if (!IS_STORYBOOK && !IS_TEST) return null
  const id = ids
    .filter(notNullish)
    .map(s => `${s}`.toLowerCase().replace(/[\W_]+/g, '-'))
    .filter(truthy)
    .join('--')
  if (!id) return null
  return {'data-testid': id}
}

/**
 * Augment style prop with display: none when hidden
 */
const displayNone = (hidden: boolean) => ({
  ...(hidden && {display: 'none'}),
})

/**
 * Drop styled-system props, keeping the rest.
 *
 * The classic branch spreads onto a styled component, which filters these out
 * itself (`shouldForwardProp` in `quickstart/styled-components/system`). A
 * reskin branch spreads onto a plain element, where an unfiltered `pl` or `mt`
 * would reach the DOM as an invalid attribute — and would be asking this
 * project to position something the admin's own layout primitives position.
 */
const domProps = <P extends object>(props: P) =>
  Object.fromEntries(
    Object.entries(props).filter(([k]) => notSystemProp(k)),
  ) as Record<string, unknown>

export type {Admin, GlobalMigrate, Migrate} from 'quickstart/types'
export * from './hooks'
export {Box, Tbody, Td, Tfoot, Th, Thead, Tr} from './styles'
export * from './utils'

/**
 * Give an element the reskin's treatment when the reskin is rendering.
 *
 * A wrapper per element rather than one branch, because the call sites are
 * `<A.Table>`, `<A.P>`, `<A.Vr>` and so on, and the branch has to reach each
 * of them. `$reskin` is transient, so it styles without reaching the DOM.
 */
const reskinStyled = (Component: any, displayName: string) => {
  const Wrapper = (props: any) => <Component $reskin={useReskin()} {...props} />
  Wrapper.displayName = displayName
  return Wrapper
}

/**
 * A table whose cells may span rows or columns, styled with the admin's
 * table-layout treatment (`.t-table`) under the reskin. The only branch left
 * here is the classic-only margin the admin has no equivalent rule for; see
 * styles.ts's comment on {@link S.Table}.
 */
export const Table = reskinStyled(S.Table, 'A.Table')

/**
 * A block of text.

 * Under the reskin it carries no margin of its own. Separation between
 * siblings is the enclosing stack's gap, which is how the admin writes the
 * same thing — `security.ftl`'s lead-in sentence is a plain child of a
 * `t-stack` with nothing on it. Classic keeps `margin: 1rem 0`.
 *
 * Not `.t-prose`: that is a *wrapper*, whose two rules space a run of prose's
 * children and restore list markers inside it. A single paragraph has neither,
 * so the class would be inert — see tasks.md's register, A.9.
 */
export const P = reskinStyled(S.P, 'A.P')

/** A vertical rule. Under the reskin, in the admin's own border color. */
export const Vr = reskinStyled(S.Vr, 'A.Vr')

/**
 * Title for a {@link Group} of fields.
 *
 * Under the reskin it is a heading one level below what already titles the
 * surface this project renders into — the design tab's own `<h2>` section
 * heading, or the block-properties dialog's `<h2>` title — so it renders as
 * `<h3 class="t-design-subhead">` and carries no styling of this project's
 * own. Under classic it keeps the styled-components rendering untouched.
 *
 * The size is cubchicken's: `.t-design-section-hd` and `.t-design-subhead`
 * each have one call site in the admin's own stylesheet, and growing them to
 * their intended sizes needs no release here (tasks.md — Workflow
 * follow-up). Until that change lands, group titles render smaller than they
 * did under classic.
 */
export const GroupTitle = ({children, ...props}: ComponentProps<'h3'>) =>
  useReskin() ? (
    <h3 className="t-design-subhead" {...props}>
      {children}
    </h3>
  ) : (
    <S.GroupTitle {...props}>{children}</S.GroupTitle>
  )

interface GroupProps extends Omit<ComponentProps<typeof S.Group>, 'title'> {
  hidden?: boolean
  title?: ReactNode
}

/**
 * A titled run of field rows — the block admins' main structural unit, and
 * over a third of all their call sites.
 *
 * Under the reskin it is the admin's own vertical layout primitive: a
 * `.t-stack` whose `data-gap="5"` is the 20px the object editor spaces its
 * property rows by (`.object-props-list`), so a block-config field row sits
 * the same distance from its neighbour as a content-properties one. Classic's
 * 20px side padding goes, because under the reskin the dialog owns its own
 * padding; classic's margins go, because the reskin lays out with gaps only
 * (`.t-stack`'s `--t-gap` defaults to 0 and is opt-in).
 *
 * The wrapper element and the trailing `<hr>` are classic-only. Under the
 * reskin the block-properties dialog is refounded — `<dialog class="t-dialog">`
 * with the form in `.t-dialog-body` and the buttons in a
 * `<footer class="t-dialog-footer">` (cubchicken
 * `admin/ActionAdminAjaxVisualDesign/layout_edit.ftl`). That footer carries
 * `border-top: 1px solid var(--t-neutral-150)` and the body carries
 * `padding: var(--t-space-5)`, so the admin already draws both the separator
 * and the space the `<hr>` was there to provide; rendering it too would put a
 * second line immediately above the admin's own. See tasks.md — 2.4.
 */
export const Group = ({
  children,
  hidden = false,
  style,
  title,
  ...props
}: GroupProps) => {
  const {global} = useAdminContext() as any
  const heading = title ? <GroupTitle>{title}</GroupTitle> : null
  if (useReskin()) {
    return (
      <div
        className="t-stack"
        data-gap="5"
        style={{...displayNone(hidden), ...style}}
        {...(props as any)}
      >
        {heading}
        {children}
      </div>
    )
  }
  return (
    <S.GroupWrapper style={displayNone(hidden)}>
      <S.Group style={style} {...props}>
        {heading}
        {children}
      </S.Group>
      {!global && <hr />}
    </S.GroupWrapper>
  )
}

const SubGroupTitle = ({children, ...props}: ComponentProps<'div'>) =>
  useReskin() ? (
    <div className="t-label" {...props}>
      {children}
    </div>
  ) : (
    <S.SubGroupTitle {...props}>{children}</S.SubGroupTitle>
  )

type FragmentElement = ReactElement<
  ComponentProps<typeof Fragment>,
  typeof Fragment
>

const isFragmentElement = (child: unknown): child is FragmentElement =>
  (child as any)?.type === Fragment

const defrag: typeof Children.toArray = children =>
  Children.toArray(children).flatMap(child =>
    isFragmentElement(child) ? defrag(child.props.children) : [child],
  )

export const SubGroup = ({
  children,
  outlined,
  shaded,
  title,
  ...props
}: ComponentProps<typeof S.SubGroup> & {title?: string}) => {
  if (!useReskin()) {
    return (
      <S.SubGroup outlined={outlined} shaded={shaded} {...props}>
        {title && <SubGroupTitle>{title}</SubGroupTitle>}
        {children}
      </S.SubGroup>
    )
  }
  // Styled-system props are dropped rather than forwarded: the only one any
  // call site passes is HeaderBlock's pl="20px", which indents a subgroup to
  // line up with classic's 20px group padding. There is no such padding under
  // the reskin, so the indent has nothing to line up with.
  const rest = domProps(props)
  return outlined || shaded ? (
    <fieldset className="t-fieldset" {...rest}>
      {title && <legend>{title}</legend>}
      {children}
    </fieldset>
  ) : (
    <div className="t-stack" data-gap="4" {...rest}>
      {title && <SubGroupTitle>{title}</SubGroupTitle>}
      {children}
    </div>
  )
}

/**
 * Layout for a tab panel, applied only while it is the selected one.
 *
 * `hidden` is a user-agent-level `display: none`, so *any* author `display`
 * declaration beats it — a `.t-stack` panel would carry `display: flex` and
 * every panel would render at once. The admin has already hit this and says
 * so: `.t-button[hidden]` exists in `admin/tizra.css` precisely because
 * "[hidden] alone loses to .t-button's own `display: inline-flex`".
 *
 * So the class goes on only when the panel is shown, which needs no rule of
 * ours and no second way of hiding things. A hidden panel has no layout to
 * describe anyway.
 *
 * The stack itself is not decoration: with `Group`'s margins gone (task 2.1),
 * successive groups in a panel have nothing to space them but this gap.
 */
const tabPanelLayout = (active: boolean | undefined) =>
  active ? {className: 't-stack', 'data-gap': '5'} : {}

/**
 * One tab in a {@link Tabs} bar.
 *
 * The admin's `.t-tab` is section *navigation* — anchors that navigate, marked
 * `.active`. These switch panels in place, so the reskin branch supplies the
 * real tablist roles *and* sets `.active`, so the admin's rule still matches
 * (design.md — Decision 6).
 */
const TabButton = ({
  active,
  children,
  ...props
}: Omit<ComponentProps<typeof S.TabButton>, 'children'> & {
  children: string
}) => {
  const testId = tid('tab', children.split(/\s/)[0])
  return useReskin() ? (
    <button
      type="button"
      role="tab"
      aria-selected={!!active}
      className={classNames('t-tab', active && 'active')}
      {...testId}
      {...(domProps(props) as ComponentProps<'button'>)}
    >
      {children}
    </button>
  ) : (
    <S.TabButton active={active} {...testId} {...props}>
      {children}
    </S.TabButton>
  )
}

const TabPane = ({
  active,
  children,
  ...props
}: ComponentProps<typeof S.TabPane>) => {
  // If there's no Group in the Tab to provide an hr between content and
  // buttons, go ahead and wrap the content in a Group.
  const hasGroup = defrag(children).some(
    child =>
      isValidElement(child) && (child.type === Group || child.type === SubTabs),
  )
  const content = hasGroup ? children : <Group>{children}</Group>
  return useReskin() ? (
    <div role="tabpanel" hidden={!active} {...tabPanelLayout(active)}>
      {content}
    </div>
  ) : (
    <S.TabPane active={active} {...props}>
      {content}
    </S.TabPane>
  )
}

const TabStrip = ({children, ...props}: ComponentProps<typeof S.TabStrip>) =>
  useReskin() ? (
    <div className="t-tab-bar" role="tablist" {...domProps(props)}>
      {children}
    </div>
  ) : (
    <S.TabStrip {...props}>{children}</S.TabStrip>
  )

interface TabsProps {
  children: ReactNode
}

export const Tabs = ({children}: TabsProps) => {
  const [activeIndex, setActiveIndex] = useState(0)

  const childArray = defrag(children)
    .filter(isValidElement)
    .filter(R.path('props.title'))

  const adminIndex = childArray.length

  const [adminTab, setAdminTab] = useState<boolean | null>(null)

  const adminGroupRef = useRef<HTMLDivElement>(null)

  const strip = (
    <TabStrip>
      {childArray.map(
        // @ts-expect-error
        ({props: {title}}, i) => (
          <TabButton
            key={i}
            active={i === activeIndex}
            onClick={() => setActiveIndex(i)}
          >
            {title}
          </TabButton>
        ),
      )}
      {adminTab !== false && (
        <TabButton
          active={activeIndex === adminIndex}
          onClick={() => setActiveIndex(adminIndex)}
        >
          Admin
        </TabButton>
      )}
    </TabStrip>
  )

  const panes = (
    <>
      {childArray.map((child, i) => (
        <TabPane key={i} active={i === activeIndex}>
          {
            // unwrap Tab (don't render as div) so TabPane can check for Group,
            // so that we get an hr between the content and the buttons at the
            // bottom of the dialog.
            // @ts-expect-error
            child?.props?.children || child
          }
        </TabPane>
      ))}
      {adminTab !== false && (
        <TabPane active={activeIndex === adminIndex}>
          <Group
            ref={adminGroupRef}
            style={{
              paddingLeft: '0',
              paddingRight: '0',
              fontSize: 'inherit',
            }}
          />
        </TabPane>
      )}
    </>
  )

  // Move admin fields into the Admin tab on mount. This moves them from outside
  // the React root into it, so we have to make sure to restore them to their
  // original position when the React root is destroyed.
  //
  // This must be useLayoutEffect instead of useEffect, otherwise the cleanup
  // function is deferred until after the component unmounts, and it doesn't run
  // in time to restore the form inputs.
  useLayoutEffect(() => {
    const limb = log.logger('Tabs.useLayoutEffect')

    const moveTo = adminGroupRef.current
    if (!moveTo) {
      limb.error(`moveTo is undefined`)
      return
    }
    const moveFromId = '#dialog-layout-block-properties-core'
    const moveFrom = moveTo.closest(moveFromId) as HTMLDivElement

    if (!moveFrom) {
      // Except in Storybook admin panel, moveFrom should never be undefined.
      limb.assert(IS_DEV, `can't find ${moveFromId}`)
      return
    }

    // The admin marks its administration region with this id when it has
    // one (cubchicken reskin-wave6-block-configs, coupling 16 in its
    // docs/evergreen.md), rather than this project finding it by scanning
    // moveFrom's children for a heading whose text reads "Administration".
    // Preferred when present, so a refounded admin can lay the region out
    // however it likes -- wrap it, give it a t-stack's gap -- without this
    // project noticing or removing anything. The scan below stays as a
    // fallback for the classic admin and any admin released before the hook
    // existed; it is not folded into the same pipeline as the hook lookup,
    // because the two differ in the one respect that matters most -- the
    // scan's branch removes a node and the hook's does not -- and that
    // difference belongs in the branch, not buried in shared data flow. It
    // retires only when classic does, alongside couplings 4 and 5.
    const hook = moveFrom.querySelector(
      '#dialog-layout-block-properties-admin',
    ) as HTMLElement | null

    let dropMe: ChildNode | undefined
    let moveMe: ChildNode[]
    // Where to restore moveMe's element(s) on unmount. Only the hook path
    // needs this: its one element need not be moveFrom's last child, while
    // the scan's run always was, so appendChild remains correct there.
    let anchor: ChildNode | null = null

    if (hook) {
      moveMe = [hook]
      anchor = hook.nextSibling
    } else {
      const allChildren = Array.from(moveFrom.childNodes)
      ;[dropMe, ...moveMe] = R.piped(
        allChildren,
        dropWhile((x: ChildNode) => x.textContent?.trim() !== 'Administration'),
        takeWhile(
          (x: ChildNode) =>
            x.nodeType !== 1 ||
            !(x as HTMLElement).querySelector('input[type=submit]'),
        ),
      )
    }
    if (dropMe) {
      limb.debug?.('dropping', dropMe)
      dropMe.remove()
    }
    moveMe.forEach(x => {
      limb.debug?.('moving', x)
      moveTo.appendChild(x)
    })
    setAdminTab(limb.pipe('setAdminTab')(!!moveMe.length))

    // Move fields out of the React root on unmount so the fields aren't lost,
    // and can be rolled up in sendUpdateBlockPropertiesRequest (cubchicken
    // aa_admin/ajaxFuncs.js). The hook path restores to the position it
    // remembered above (insertBefore(x, null) is appendChild, so a hook that
    // was last needs no special case); the scan path's run always was the
    // tail of moveFrom, so appending is unchanged.
    return () => {
      limb.debug?.('running cleanup')
      moveMe.forEach(x => {
        limb.debug?.('restoring', x)
        if (hook) {
          moveFrom.insertBefore(x, anchor)
        } else {
          moveFrom.appendChild(x)
        }
      })
    }
  }, [])

  // Under the reskin the bar and the panes need something to space them, and
  // the panes need a container that isn't the dialog body.
  return useReskin() ? (
    <div className="t-stack" data-gap="4">
      {strip}
      {panes}
    </div>
  ) : (
    <>
      {strip}
      {panes}
    </>
  )
}

interface TabProps {
  title: string
  children: ReactNode
}

/**
 * This never actually renders as a div, unless it's accidentally used outside
 * of <Tabs>.
 */
export const Tab = ({title, ...props}: TabProps) => <div {...props} />

const SubTabPane = ({
  active,
  children,
  ...props
}: ComponentProps<typeof S.SubTabPane>) => {
  // If there's no Group in the Tab to provide an hr between content and
  // buttons, go ahead and wrap the content in a Group.
  const hasGroup = defrag(children).some(
    child => isValidElement(child) && child.type === Group,
  )
  const content = hasGroup ? children : <Group>{children}</Group>
  return useReskin() ? (
    <div role="tabpanel" hidden={!active} {...tabPanelLayout(active)}>
      {content}
    </div>
  ) : (
    <S.SubTabPane active={active} {...props}>
      {content}
    </S.SubTabPane>
  )
}

const SubTabStrip = ({
  children,
  ...props
}: ComponentProps<typeof S.SubTabStrip>) =>
  useReskin() ? (
    <div
      className="t-tab-bar"
      role="tablist"
      data-level="2"
      {...domProps(props)}
    >
      {children}
    </div>
  ) : (
    <S.SubTabStrip {...props}>{children}</S.SubTabStrip>
  )

/**
 * One tab in a {@link SubTabs} bar. The admin has a single tab vocabulary, so
 * a second-level bar is the same `.t-tab-bar`/`.t-tab` as the first; classic's
 * pipe separators go, because the bar spaces its own tabs.
 */
const SubTabButton = ({
  active,
  children,
  ...props
}: Omit<ComponentProps<typeof S.SubTabButton>, 'children'> & {
  children: string
}) => {
  const testId = tid('subtab', children.split(/\s/)[0])
  return useReskin() ? (
    <button
      type="button"
      role="tab"
      aria-selected={!!active}
      className={classNames('t-tab', active && 'active')}
      {...testId}
      {...(domProps(props) as ComponentProps<'button'>)}
    >
      {children}
    </button>
  ) : (
    <S.SubTabButton active={active} {...testId} {...props}>
      <span>{children}</span>
    </S.SubTabButton>
  )
}

interface SubTabsProps {
  children: ReactNode
}

export const SubTabs = ({children}: SubTabsProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const reskin = useReskin()

  const childArray = defrag(children)
    .filter(isValidElement)
    .filter(R.path('props.title'))

  const strip = (
    <SubTabStrip>
      {childArray.map(
        // @ts-expect-error
        ({props: {title}}, i) => (
          <Fragment key={i}>
            {i !== 0 && !reskin && <span className="pipe">|</span>}
            <SubTabButton
              active={i === activeIndex}
              onClick={() => setActiveIndex(i)}
            >
              {title}
            </SubTabButton>
          </Fragment>
        ),
      )}
    </SubTabStrip>
  )

  const panes = (
    <>
      {childArray.map((child, i) => (
        <SubTabPane key={i} active={i === activeIndex}>
          {
            // unwrap SubTab (don't render as div) so SubTabPane can check for
            // Group, so that we get an hr between the content and the buttons
            // at the bottom of the dialog.
            // @ts-expect-error
            child?.props?.children || child
          }
        </SubTabPane>
      ))}
    </>
  )

  return reskin ? (
    <div className="t-stack" data-gap="4">
      {strip}
      {panes}
    </div>
  ) : (
    <>
      {strip}
      {panes}
    </>
  )
}

interface SubTabProps {
  title: string
  children: ReactNode
}

/**
 * This never actually renders as a div, unless it's accidentally used outside
 * of <SubTabs>.
 */
export const SubTab = ({title, ...props}: SubTabProps) => <div {...props} />

/**
 * A cell of a {@link Row}. Pure layout-prop carrier on both branches — `S.Box`
 * declares nothing of its own, it only materializes the `width`, `maxWidth` and
 * `opacity` its call sites pass. There is no classic appearance here to
 * convert, so there is nothing to branch.
 */
export const RowItem = S.Box

/**
 * A horizontal run of controls.
 *
 * Under the reskin the flex direction and the spacing come from the admin's
 * `.t-row`, whose `data-gap="3"` is the same 12px classic hard-coded — and
 * whose `align-items: center` is the same thing three call sites spell out as
 * `alignItems="center"`.
 *
 * Both branches keep `S.Box` underneath, because call sites pass sizing props
 * (`maxWidth`, `width`, `opacity`) that are per-call-site layout and not a
 * reproduction of the classic look — design.md, Non-Goals: styled-components
 * is permitted on the reskin branch where it earns its place. Spreading those
 * onto a plain element instead would put `maxWidth="30%"` in the DOM as an
 * attribute.
 */
export const Row = ({children, ...props}: ComponentProps<typeof S.Row>) =>
  useReskin() ? (
    <S.Box className="t-row" data-gap="3" {...props}>
      {children}
    </S.Box>
  ) : (
    <S.Row {...props}>{children}</S.Row>
  )

/**
 * A vertical run of controls, more tightly spaced than a {@link Group}.
 * `data-gap="1"` is classic's 4px on the admin's own scale.
 */
export const Column = ({
  children,
  ...props
}: ComponentProps<typeof S.Column>) =>
  useReskin() ? (
    <S.Box className="t-stack" data-gap="1" {...props}>
      {children}
    </S.Box>
  ) : (
    <S.Column {...props}>{children}</S.Column>
  )

/**
 * A run of controls in either direction, spaced a step wider than a
 * {@link Row}. `data-gap="4"` is classic's 16px on the admin's own scale.
 *
 * Classic's `flex: 1 1 auto; align-self: stretch` is not carried over: a
 * `.t-stack` child of a `.t-stack` already stretches, which is the only
 * arrangement any call site puts one in.
 *
 * Accepts (and forwards) a `ref` so it can stand in as a {@link FieldArray}'s
 * `droppableAs`/`draggableAs`, which hands `@hello-pangea/dnd` a ref to
 * measure. React 19 passes `ref` through as a normal prop, so this needs no
 * `forwardRef`.
 */
export const Stack = ({
  children,
  row,
  ref,
  ...props
}: ComponentProps<typeof S.Stack> & {ref?: Ref<HTMLDivElement>}) =>
  useReskin() ? (
    <S.Box
      ref={ref}
      className={row ? 't-row' : 't-stack'}
      data-gap="4"
      {...props}
    >
      {children}
    </S.Box>
  ) : (
    <S.Stack ref={ref} row={row} {...props}>
      {children}
    </S.Stack>
  )

type Deriver = (props: {from: string; value: any; prev: any; to: string}) => any

type Cascader = {from: string; to: string; derive: Deriver}

interface UseCascadeProps {
  cascade:
    | {from: string | string[]; derive: Deriver}
    | {to: string | string[]; derive: Deriver}
    | Cascader[]
    | Falsish
  name: string
}

const useCascade = ({cascade, name}: UseCascadeProps): ReactElement[] => {
  if (!cascade) return []
  return (Array.isArray(cascade) ? cascade : [])
    .concat(
      ensureArray((cascade as any)?.to).map((to: string) => ({
        ...cascade,
        from: name,
        to,
      })) as Cascader[],
      ensureArray((cascade as any)?.from).map((from: string) => ({
        ...cascade,
        from,
        to: name,
      })) as Cascader[],
    )
    .map(({from, to, derive}) => (
      <Final.Field key={to} name={to} subscription={{}}>
        {(
          // No subscription. We only use Field to get the change function.
          {input: {onChange: changeTo}},
        ) => (
          <Listen.OnChange name={from}>
            {(value, prev) => {
              const derived = derive({from, value, prev, to})
              if (!R.isNil(derived)) {
                changeTo(derived)
              }
            }}
          </Listen.OnChange>
        )}
      </Final.Field>
    ))
}

/**
 * The client-built field row from design.md — Decision 2, shape A:
 * `.t-field > .t-field-lbl > .t-field-lbl-txt` + `.t-field-lbl-hint`, then
 * `.t-field-ctl` around the control. Structurally identical to what
 * `<t-field>` produces for itself, so a row this project builds sits beside a
 * row the admin's component produces with no visible seam.
 *
 * Exported because `A.MarkdownEditor` is the one control that does not pass
 * through {@link adminField} — it has a hack-gated fork above the field, so
 * the wrapper cannot sit outside it — and would otherwise be the only field in
 * the surface whose label and hint are not in a label row.
 *
 * Its own component, rather than a branch inside {@link adminField}, so that
 * the `useId` it needs is never called on the classic path — an extra `useId`
 * there shifts every generated id in the form, which is a classic-mode diff
 * (design.md — Decision 9).
 *
 * `control` is a function of the generated id because the label row is
 * rendered above the control that id belongs to.
 */
export const ReskinFieldRow = ({
  cascaders,
  control,
  group,
  hidden,
  hint,
  id: idProp,
  label,
}: any) => {
  // Most callers let this generate its own id. A.Taggers is the one caller
  // that can't: t-tagpicker opens by document.getElementById on its trigger
  // attributes, so the three Tagger fields it wires to need ids fixed before
  // render rather than discovered from one. Passing that id through here
  // keeps the label's htmlFor pointed at the control it actually labels,
  // rather than at whatever this hook would have generated on its own.
  const generatedId = useId()
  const id = idProp || generatedId
  return (
    <div className="t-field" style={displayNone(!!hidden)}>
      {(!!label || !!hint) && (
        <div className="t-field-lbl">
          {!!label &&
            (group ? (
              // Several controls under one label, so it cannot be a
              // `<label for>` pointing at any one of them. Named instead, for
              // the control to reference with aria-labelledby once tasks
              // 5.2/5.3 restructure it.
              <span className="t-field-lbl-txt" id={`${id}-label`}>
                {label}
              </span>
            ) : (
              <label className="t-field-lbl-txt" htmlFor={id}>
                {label}
              </label>
            ))}
          {!!hint && <span className="t-field-lbl-hint">{hint}</span>}
        </div>
      )}
      <div className="t-field-ctl">{control(id)}</div>
      {cascaders}
    </div>
  )
}

/**
 * Wrap a bare control in a field row — its label, hint and cascade listeners.
 *
 * Under the reskin the row is {@link ReskinFieldRow}. The hint moves into the
 * label row, which is where the admin puts it (design.md — Decision 8), and
 * the control below stops rendering one of its own. Building the row here
 * rather than in each control is what relocates the hint for every field at
 * once, and leaves tasks 4.x/5.x to produce only the control element.
 *
 * `defaults` carries two opt-outs for controls the plain shape doesn't fit:
 *
 * - `ownLabel` — the label belongs beside the control, not above it, so no
 *   label row is built and `label`/`hint` are passed down unchanged.
 *   CheckLeft/CheckRight; the admin's shape for these is `.t-checkline`
 *   (task 5.4).
 * - `group` — the field is several controls, so no `id` is handed down either;
 *   it would land on every radio in the group.
 * - `ownRow` — the admin's `<t-field>` builds the whole row for itself
 *   (design.md — Decision 2, shape B), so none is built here and `label`,
 *   `hint` and `hidden` are passed down for it to render. A predicate rather
 *   than a flag, because for `A.Input` it turns on the props: a field with a
 *   datalist has no shape-B route (design.md — Decision 3, fourth condition),
 *   and a field with no label wants no label row at all.
 */
const adminField = (displayName: any, Component: any, defaults?: any) => {
  // This must be dynamic (instead of moving ComponentWrapper to the top level)
  // so we can set displayName.
  const ComponentWrapper = (props: any) => {
    let {
      cascade,
      colon = true,
      group,
      hidden,
      hint,
      id: idOverride,
      label,
      name,
      ownLabel,
      ownRow,
      snug,
      ...rest
    }: any = {...defaults, ...props}
    const cascaders = useCascade({name, cascade})
    const buildsOwnRow =
      typeof ownRow === 'function' ? ownRow({...defaults, ...props}) : !!ownRow

    // A trailing colon belongs to classic's right-aligned label gutter. The
    // reskin stacks the label above the control and the admin's own
    // `.t-field-lbl-txt` carries none, so it would be ours alone.
    if (!useReskin()) {
      if (colon && typeof label === 'string' && /\w$/.test(label)) {
        label += ':'
      }
      return (
        <S.AdminField hidden={hidden} snug={snug}>
          <Component name={name} label={label} hint={hint} {...rest} />
          {cascaders}
        </S.AdminField>
      )
    }

    if (buildsOwnRow) {
      return (
        <>
          <Component
            name={name}
            label={label}
            hint={hint}
            hidden={hidden}
            ownRow
            {...rest}
          />
          {cascaders}
        </>
      )
    }

    return (
      <ReskinFieldRow
        cascaders={cascaders}
        group={group}
        hidden={hidden}
        hint={!ownLabel && hint}
        id={idOverride}
        label={!ownLabel && label}
        control={(id: string) => (
          <Component
            name={name}
            {...(ownLabel && {label, hint})}
            {...(group
              ? // No `id` to hand down — it would land on every control in the
                // group — so the label row names itself instead and the group
                // points back at it, which is what makes the field's own label
                // reach a set of radios or checkboxes at all.
                //
                // Only when there is a label to point at: several call sites
                // give a group no label of its own and let the enclosing
                // `A.SubGroup` title carry it, and an `aria-labelledby`
                // naming an element that isn't in the document is worse than
                // none — it is a reference a screen reader follows to nothing.
                {labelledBy: label ? `${id}-label` : undefined}
              : {id})}
            {...rest}
          />
        )}
      />
    )
  }
  Component.displayName = `A.${displayName}`
  ComponentWrapper.displayName = `A.${displayName}Wrapper`
  return ComponentWrapper
}

/**
 * A field's label. Under the reskin, the label-row text the admin puts above
 * a control.
 *
 * Also still used to wrap an individual radio or checkbox with its option
 * text, which wants `.t-radio`/`.t-checkbox` instead — tasks 5.2–5.4.
 */
export const Label = ({children, ...props}: ComponentProps<typeof S.Label>) =>
  useReskin() ? (
    <label className="t-field-lbl-txt" {...domProps(props)}>
      {children}
    </label>
  ) : (
    <S.Label {...props}>{children}</S.Label>
  )

/**
 * A field's explanatory text. Under the reskin it belongs in the label row
 * (design.md — Decision 8), which is where {@link adminField} puts it;
 * rendered directly, as `FooterBlock` and `NavBlock` do inside a bare
 * {@link AdminField}, it is a muted line of its own.
 */
export const Hint = ({children, ...props}: ComponentProps<typeof S.Hint>) =>
  useReskin() ? (
    <span className="t-field-lbl-hint" {...domProps(props)}>
      {children}
    </span>
  ) : (
    <S.Hint {...props}>{children}</S.Hint>
  )

/**
 * A field row. Under the reskin, the admin's own `.t-field` box — the same one
 * `<t-field>` puts on itself, so a row this project builds and a row the
 * admin's component produces are the same box (design.md — Decision 2).
 *
 * `snug` has no reskin equivalent and needs none: it trimmed classic's
 * margins, and the reskin spaces rows with the enclosing stack's gap.
 */
export const AdminField = ({
  children,
  hidden,
  snug,
  ...props
}: ComponentProps<typeof S.AdminField>) =>
  useReskin() ? (
    <div className="t-field" style={displayNone(!!hidden)} {...domProps(props)}>
      {children}
    </div>
  ) : (
    <S.AdminField hidden={hidden} snug={snug} {...props}>
      {children}
    </S.AdminField>
  )

/**
 * Final.Field that preserves empty string values.
 * https://final-form.org/docs/react-final-form/types/FieldProps#parse
 */
export const Field = (({
  ref,
  component: Component,
  format,
  name,
  parse = R.identity,
  ...props
}: any) => {
  const fieldProps = {format, name, parse, ref}
  return typeof Component === 'string' ? (
    <Final.Field component={Component} {...fieldProps} {...props} />
  ) : Component ? (
    <Final.Field {...fieldProps} {...props}>
      {({input, meta}) => <Component {...props} {...input} {...meta} />}
    </Final.Field>
  ) : (
    <Final.Field {...fieldProps} {...props} />
  )
}) as typeof Final.Field<any>

/**
 * The bare element, kept out of {@link ReskinField} so its identity is stable
 * across renders — an inline `component` would remount the field, and with it
 * the admin's control, on every keystroke.
 *
 * Attributes are listed one by one rather than spread. Final Form hands its
 * render prop a whole `meta` bag beside `input`, and a custom element this
 * project does not own is the wrong place to find out what leaks onto the DOM.
 *
 * The edit is picked up by an explicit listener rather than by React's
 * `onInput`. The synthetic route does work — the `input` event bubbles out of
 * the Lit-rendered control, and React's root listener walks up to the nearest
 * fiber, which is this element — but it works by two behaviors nothing
 * guarantees: that React's delegation finds a fiber for a subtree React never
 * created, and that `onInput` on a custom element maps to native `input` at
 * all (React maps `onChange` to native `change`, which fires only on commit).
 * An `addEventListener` on the element in hand depends on neither.
 *
 * The listener binds once per mount and reads the current `onChange` through a
 * ref, because Final Form hands out a fresh one every render; re-binding on
 * each would tear down and re-attach the listener on every keystroke.
 */
/**
 * Whether the admin actually loaded on this page understands `untracked`.
 *
 * `context.reskin` only says the admin renders the reskin shell; it does not
 * say which cubchicken release that shell shipped from. `untracked` landed in
 * a later change to `<t-field>` than the reskin switch itself, so a reskin
 * admin can still predate it -- and that admin's `_tracks` getter reads
 * `!!fieldId && baseline != null` with no third operand, so it counts a
 * baseline alone as reason to track the field. Evergreen and cubchicken are
 * separate deploys with no guarantee of landing atomically, so gating on
 * `reskin` alone would arm the save model on an admin this project never
 * coordinated the migration with. Feature-detecting the registered element's
 * own `properties` (mirroring the `typeof` guard the admin already asks
 * callers to use for `setValue`, design.md -- Decision 1) answers the actual
 * question instead of assuming a release order.
 */
const supportsUntrackedField = () =>
  !!(globalThis.customElements?.get('t-field') as any)?.properties?.untracked

const ReskinFieldControl = ({
  controlType,
  fieldId,
  hidden,
  hint,
  label,
  name,
  onChange,
  placeholder,
  value,
}: any) => {
  const ref = useRef<HTMLElement>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const reskin = useReskin()
  // Both the tracking opt-out and the baseline that would otherwise arm it
  // are withheld unless this admin's `<t-field>` actually supports
  // `untracked` -- see `supportsUntrackedField`.
  const untracked = reskin && supportsUntrackedField()
  // The as-loaded value, captured once at mount rather than read from the
  // live `value` prop -- which Final Form updates on every keystroke and
  // would make `baseline === value` forever, so the field could never be
  // shown as modified. Capturing per mount rather than per field id is
  // correct here: reopening a block-config dialog remounts this control, so
  // the baseline is the freshly loaded config, which is what the admin would
  // have loaded too (design.md -- Decision 4).
  //
  // Captured only when `untracked` is supported: on an admin that isn't,
  // supplying a baseline at all would arm that admin's save model, since its
  // `_tracks` getter has no `untracked` operand to decline with.
  const capturedRef = useRef(false)
  const baselineRef = useRef<string | undefined>(undefined)
  if (!capturedRef.current) {
    capturedRef.current = true
    baselineRef.current = untracked ? (value ?? '') : undefined
  }
  useEffect(() => {
    const el = ref.current
    if (!el) return
    // Undo and redo report themselves as ordinary `input` events from the
    // control, so they arrive here too and need no separate wiring.
    const listen = (e: Event) =>
      onChangeRef.current((e.target as HTMLInputElement).value)
    el.addEventListener('input', listen)
    return () => el.removeEventListener('input', listen)
  }, [])
  return (
    <t-field
      baseline={baselineRef.current}
      control-type={controlType}
      field-id={fieldId}
      hint={hint || undefined}
      label={label || undefined}
      name={name}
      placeholder={placeholder}
      ref={ref}
      style={displayNone(!!hidden)}
      untracked={untracked || undefined}
      value={value ?? ''}
    />
  )
}

/**
 * design.md — Decision 2, shape B: the admin's own field component builds the
 * whole row for itself — label, hint, control, modified marker and undo — so
 * this project renders one element and gets out of its way.
 *
 * **`baseline` is the as-loaded value, and `untracked` is what keeps it from
 * joining the admin's save model.** `<t-field>` reads dirtiness as
 * `baseline != null && value !== baseline`, so before this change these forms
 * withheld the baseline entirely to stay out of that projection. That relied
 * on the baseline being the only closed gate: `field-id` is already supplied,
 * because `<t-field>`'s `<label for>` needs it, so a baseline alone would open
 * the admin's shared change tracking too — `ftest`, the `-h` twin, the
 * unsaved-changes warning. `ReskinFieldControl` now supplies both a baseline
 * and the `untracked` attribute, so the marker and undo work while the save
 * model stays declined explicitly rather than by omission (design.md —
 * Decision 4). Both are withheld together on an admin `supportsUntrackedField`
 * finds no support in, falling back to the pre-Decision-4 shape rather than
 * trusting that a reskin admin ships `untracked` alongside `reskin` itself.
 */
const ReskinField = (props: any) => (
  <Field component={ReskinFieldControl} {...props} />
)

/**
 * The bare `<t-combobox>` element, kept out of {@link ComboboxField} for the
 * same reason {@link ReskinFieldControl} is kept out of {@link ReskinField}:
 * an inline `component` would remount the field, and with it the admin's
 * TomSelect instance, on every keystroke.
 *
 * The admin's own change bubbles as a `change` `CustomEvent` with
 * `detail.value` — not the native `change` a plain `<input>` fires on commit —
 * so it needs the same `addEventListener` bridge `t-field`'s `input` does
 * (design.md — Decision 3, tasks.md — 2.1) rather than React's synthetic
 * `onChange`, which maps to native `change` and would also mean guessing at
 * what shape of event a custom element's `onChange` prop receives.
 *
 * `options` arrives as this project's `{value, label}[]` and is flattened to
 * the semicolon-joined value list the element's `options` attribute wants;
 * the admin unions it with whatever loads from `prop` when both are given.
 * `no-raw` is passed unconditionally, as a real boolean rather than the
 * attribute-presence `""` JSX shorthand — design.md — Decision 4 turns the
 * raw-tagstring escape hatch off for every control this project hands a row
 * to, not only the ones that happen to pass `options`.
 *
 * `multiple` is the element's mode switch, not a hint: without it TomSelect
 * runs at `maxItems: 1`, so a stored semicolon list reads as a single item and
 * the next pick replaces the whole list. Every field whose value goes through
 * `semiSplit` — the admin-tag fields, via `adminTagged` — has to pass it.
 *
 * Both booleans are passed as `true | undefined` — matching their
 * `elements.d.ts` type — never the `'' : undefined` idiom this project
 * otherwise uses for boolean attributes. React 19 sets a prop on a custom
 * element as a JS property rather than an HTML attribute whenever the element
 * already exposes an accessor of that exact name, and a direct property set
 * skips Lit's attribute-to-property boolean conversion entirely. `t-combobox`
 * is a Lit element with `multiple` in its `static properties`, spelled the
 * same way in both places, so `multiple={''}` would hand `components.mjs` the
 * literal empty string, and `this.multiple ? null : 1` reads that as falsy:
 * single-select, with nothing in the DOM's attribute list to reveal why.
 * `no-raw` happens to dodge this today only because its reactive property is
 * actually named `noRaw` — no accessor named `no-raw` exists for React to
 * find — but that is a naming coincidence, not a guarantee, so it is written
 * as the real boolean it is rather than leaning on it.
 */
const ReskinComboboxControl = ({
  hidden,
  id,
  multiple,
  name,
  onChange,
  options,
  prop,
  value,
}: any) => {
  const ref = useRef<HTMLElement>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const listen = (e: Event) => {
      // TomSelect renders its `<input>` into `t-combobox`'s own light DOM and
      // fires a bubbling native `change` on it beside the element's custom
      // one. That event carries no `detail`, so forwarding it blanks the field
      // an instant after the real value lands. The admin's own consumer guards
      // the same way (`components.mjs`, `_valueControl`), and `t-input`'s
      // header comment names the hazard from the other side.
      const {detail} = e as CustomEvent
      if (detail == null) return
      onChangeRef.current(detail.value ?? '')
    }
    el.addEventListener('change', listen)
    return () => el.removeEventListener('change', listen)
  }, [])
  return (
    <t-combobox
      id={id}
      multiple={multiple || undefined}
      name={name}
      no-raw={true}
      options={
        options?.length
          ? semiJoin((options as OptionVL[]).map(o => `${o.value}`))
          : undefined
      }
      prop={prop || undefined}
      ref={ref}
      style={displayNone(!!hidden)}
      value={value ?? ''}
    />
  )
}

/**
 * A property-bound (or statically optioned) value picker — design.md —
 * Decisions 1, 1b, 2 and 4. No `type`/`meta-type` is ever passed: the
 * element's own default is `AdminTagged`, which is exactly what Decision 1b
 * asks for, so there is nothing to override.
 */
const ComboboxField = (props: any) => (
  <Field component={ReskinComboboxControl} {...props} />
)

export const Input = adminField(
  'Input',
  ({fluid, hint, label, multiple, options, ownRow, prop, ...props}: any) => {
    const id = useId()
    const listId = useId()
    const reskin = useReskin()
    if (reskin && ownRow) {
      return (
        <ReskinField
          controlType="text"
          fieldId={id}
          hint={hint}
          label={label}
          {...props}
        />
      )
    }
    if (reskin && (options || prop)) {
      return (
        <ComboboxField
          multiple={multiple}
          options={options}
          prop={prop}
          {...props}
        />
      )
    }
    return (
      <div>
        <div>
          {!!label && <Label htmlFor={id}>{label}</Label>}
          <Field
            id={id}
            component={S.Input}
            type="text"
            list={listId}
            {...props}
            className={classNames(reskin && 't-input', props.className)}
            style={{
              ...(fluid && {width: '100%'}),
              ...props.style,
            }}
          />
          {!!options && (
            <datalist id={listId}>
              {(options as OptionVL[]).map(({value}, i) => (
                <option key={i} value={value} />
              ))}
            </datalist>
          )}
        </div>
        {!!hint && <Hint>{hint}</Hint>}
      </div>
    )
  },
  {
    // `<t-field>` builds a text row for itself, but has no route to a
    // datalist or to the admin's combobox: `list`/`prop` are nowhere in its
    // attributes or `<t-input>`'s, and the `<input>` is Lit-rendered out of
    // reach. Five call sites pass `options`, so those keep a client-built row
    // (design.md — Decision 3, fourth condition). The row is client-built,
    // but under the reskin the control itself is the admin's combobox rather
    // than a plain input with a datalist.
    //
    // `A.Tagger` forces `ownRow: false` explicitly (below its own definition)
    // for a different reason: it needs a plain, addressable `<input>` for
    // `t-tagpicker` to find by id, not a combobox or `<t-field>`'s own host
    // element -- see `A.Tagger`'s docstring.
    //
    // A field with no label of its own stays client-built too, because shape B
    // is for rows that have row chrome. `<t-field>` renders its `.t-field-lbl`
    // unconditionally -- an empty `<label>` still holds a line box, and
    // `.t-field`'s `gap` still applies -- so a label-less shape-B field stands
    // taller than the shape-A fields beside it, and in a `A.Row` of controls
    // whose label is implied by the column (BookshelfBlock's sort rows) that
    // reads as the control sitting too low. `ReskinFieldRow` already omits the
    // label row when there is neither label nor hint, which is the behavior
    // wanted here. The cost is the admin's modified marker and undo button,
    // which live in that row and so cannot appear on a field that has none.
    ownRow: ({label, options, prop}: any) => !!label && !options && !prop,
  },
)

interface TaggerProps {
  id?: string
  label: string
  name: string
}

/**
 * A tag field: a plain text field, exactly like classic's, on both branches.
 *
 * It used to carry the arguments for the admin's tag picker —
 * `taggerParentElId`, `currentType`, `defaultProp`, `turnOnTarget`, `options`,
 * `extraTagGatheringInfo` — and a `ref` callback whose body was a commented-out
 * `new window.tagger(...)`. Nothing called it, no call site passed any of those
 * arguments, and `window.tagger` is referenced nowhere else in this project, so
 * it was a dead coupling rather than a disabled feature (design.md —
 * Decision 7). That picker is what `A.Taggers`'s query builder replaces now;
 * see there for how the two are wired together.
 *
 * **Not the admin's combobox.** An earlier version of this bound the field to
 * the `AdminTags` property, the way the standalone typeahead fields do. That
 * doesn't fit here: `t-tagpicker` opens by attaching a `click` listener to
 * each trigger element (`document.getElementById`, in its `_attachTriggers`),
 * and a bound `t-combobox` wraps TomSelect, whose own `click` handler on
 * `.ts-control` calls `stopPropagation()` for the common case of an empty
 * field -- so the click never reaches the listener `t-tagpicker` attached to
 * the `t-combobox` host, and the dialog never opens for a real click (only a
 * synthetic one dispatched directly on the host). The admin's own standalone
 * usage never combines the two controls this way: its trigger inputs are
 * always plain `<input>`s with no competing click handler. So this matches
 * that — a plain, addressable input, on both branches — rather than
 * reproducing a combination the admin doesn't make either.
 *
 * `ownRow: false` is forced (overriding `A.Input`'s own default, which would
 * otherwise hand a field with neither `options` nor `prop` to `<t-field>`)
 * because `t-tagpicker` needs a real `<input>` in the document with a stable
 * id and an ordinary `.value` — `<t-field>`'s host is a Lit custom element,
 * not the input itself, and forcing the row to stay client-built keeps the
 * `<input>` `A.Input` already renders reachable by that id.
 *
 * Kept as a distinct component, rather than call sites using `A.Input`
 * directly, so the binding lives in one place: `A.Tagger` is reached only
 * through `A.Taggers` (design.md, Context), which is what keeps
 * `SubnavBlock`'s admin unchanged.
 */
export const Tagger: FC<TaggerProps> = props => (
  <Input ownRow={false} {...props} />
)

interface TaggersProps {
  name: string
}

/**
 * Reads a `t-tagpicker-apply` event off a ref'd element and writes all three
 * values into the form in one go (design.md — Decision 3). The admin's own
 * write into the trigger inputs (a bare `.value =` assignment) is invisible to
 * a form store and is not read here; the event is the contract, and the
 * assignment becomes a harmless duplicate the next render reconciles.
 *
 * A ref-bound `addEventListener` rather than a JSX event prop, for the same
 * reason `t-field`'s `input` bridge is one: this project doesn't own the
 * element and shouldn't guess at whether React's synthetic delegation would
 * reach a bubbling `CustomEvent` from it.
 */
const useTagpickerApply = (
  onApply: (detail: {all: string; any: string; excluded: string}) => void,
) => {
  const ref = useRef<HTMLElement>(null)
  const onApplyRef = useRef(onApply)
  onApplyRef.current = onApply
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const listen = (e: Event) => onApplyRef.current((e as CustomEvent).detail)
    el.addEventListener('t-tagpicker-apply', listen)
    return () => el.removeEventListener('t-tagpicker-apply', listen)
  }, [])
  return ref
}

/**
 * The three fields a Sub Nav block's match query serializes to, plus — under
 * the reskin — the admin's query builder, wired to them by DOM id
 * (design.md — Decision 3, revised). Ids are generated here, rather than left
 * to each Tagger's own field row, because `t-tagpicker` finds its trigger
 * elements by id and has to be given one that already exists when it mounts.
 *
 * The trigger elements are plain `<input>`s (`A.Tagger`'s own docstring),
 * exactly as they are in the admin's own standalone usage — not `t-combobox`,
 * whose TomSelect control eats the click `t-tagpicker` listens for.
 *
 * Split into its own component, rather than branched inline, so the three
 * extra `useId` calls run only when the reskin actually mounts this — exactly
 * the trap `ReskinFieldRow`'s own comment names (design.md — Decision 9): a
 * `useId` called unconditionally from `Taggers` itself would still run under
 * classic and shift every generated id after it in the form, which is a
 * classic-mode diff despite classic never seeing the picker it was for.
 */
const ReskinTaggers: FC<TaggersProps> = ({name, ...props}) => {
  const idAny = useId()
  const idAll = useId()
  const idExcluded = useId()
  const {change} = Final.useForm()
  const tagpickerRef = useTagpickerApply(({all, any, excluded}) => {
    change(`${name}.any`, any)
    change(`${name}.all`, all)
    change(`${name}.excluded`, excluded)
  })
  return (
    <>
      <Tagger {...props} id={idAny} label="Matching any" name={`${name}.any`} />
      <Tagger {...props} id={idAll} label="Matching all" name={`${name}.all`} />
      <Tagger
        {...props}
        id={idExcluded}
        label="Excluding"
        name={`${name}.excluded`}
      />
      <t-tagpicker
        ref={tagpickerRef}
        trigger-all={idAll}
        trigger-any={idAny}
        trigger-excluded={idExcluded}
      />
    </>
  )
}

/**
 * Classic renders the same three text fields it always has, and no picker:
 * `t-tagpicker` is reskin-only (design.md, Context), so the reskin's extra
 * fields and hooks live entirely in {@link ReskinTaggers}, which classic never
 * mounts.
 */
export const Taggers: FC<TaggersProps> = ({name, ...props}) =>
  useReskin() ? (
    <ReskinTaggers name={name} {...props} />
  ) : (
    <>
      <Tagger {...props} label="Matching any" name={`${name}.any`} />
      <Tagger {...props} label="Matching all" name={`${name}.all`} />
      <Tagger {...props} label="Excluding" name={`${name}.excluded`} />
    </>
  )

type OptionsObj<K extends string = string> = {
  [value in K]: ReactNode
}

type OptionVL<K extends string = string> = {
  value: K
  label: ReactNode
}

export const options = <K extends string>(os: OptionsObj<K>): OptionVL<K>[] =>
  R.toPairs(os).map(([value, label]) => ({value: value as K, label}))

export const useOptions = <K extends string>(
  os: OptionsObj<K>,
): OptionVL<K>[] => useMemo(() => options(os), [os])

const Option = (props: any) => <S.Option {...props} />

const OptionLabel = (props: any) => <S.OptionLabel {...props} />

/**
 * The longest a single choice's label can be before the set stacks, and the
 * most characters a row of them may add up to.
 *
 * A row of radios has no wrapping behavior worth having — `.t-radio-grp` is a
 * flex row — so the choice has to be made before rendering, from the only
 * thing known then: the labels. The admin makes the same call by hand and says
 * so, in the rule behind `.t-choice-stack` ("a vertical set of radios, where
 * `.t-radio-grp`'s horizontal row does not suit the labels' length"); this
 * just applies the same judgement mechanically, because these sets come from
 * twenty-odd call sites rather than one template.
 *
 * Character counts and not measured text, deliberately: the alternative is
 * measuring after layout and reflowing, which would flip a group's direction
 * after it was already on screen. The budget is sized for the narrow dialog
 * these forms render in — see tasks.md's register, A.3.
 */
const CHOICE_LABEL_MAX = 24
const CHOICE_ROW_BUDGET = 60

/**
 * Whether a set of choices stacks rather than sitting in a row. A label that
 * isn't a plain string always stacks: `ImageBlock`'s image-fitting options are
 * whole sentences in a `<span>`, and nothing can be counted about them.
 */
const stacksChoices = (options: OptionVL[]) => {
  let total = 0
  for (const {label} of options) {
    if (typeof label !== 'string' || label.length > CHOICE_LABEL_MAX) {
      return true
    }
    total += label.length
  }
  return total > CHOICE_ROW_BUDGET
}

export const Radio = adminField(
  'Radio',
  ({hint, label, labelledBy, options, ...props}: any) => {
    if (useReskin()) {
      const stacked = stacksChoices(options as OptionVL[])
      return (
        <div
          aria-labelledby={labelledBy}
          className={stacked ? 't-stack t-choice-stack' : 't-radio-grp'}
          role="radiogroup"
          {...(stacked && {'data-gap': '2'})}
        >
          {(options as OptionVL[]).map(o => (
            <label
              className="t-radio"
              key={o.value}
              {...tid(props.name, o.value)}
            >
              <Field
                component="input"
                type="radio"
                value={o.value}
                {...props}
              />
              {o.label}
            </label>
          ))}
        </div>
      )
    }
    return (
      <div style={{marginBottom: '1rem'}}>
        {!!label && <Label display="block">{label}</Label>}
        {(options as OptionVL[]).map(o => (
          <S.Option key={o.value}>
            <Label {...tid(props.name, o.value)}>
              <Field
                component={S.Input}
                type="radio"
                value={o.value}
                {...props}
              />
              <OptionLabel>{o.label}</OptionLabel>
            </Label>
          </S.Option>
        ))}
        {!!hint && <Hint>{hint}</Hint>}
      </div>
    )
  },
  // Several controls under one label, so the label cannot point at any one
  // of them. See adminField.
  {group: true},
)

/**
 * A yes/no pair the admin's field component builds — design.md — Decision 2,
 * shape B.
 *
 * `format`/`parse` are the whole boolean adapter. `<t-radio-pair>` submits one
 * of the two literal strings `"true"`/`"false"` and reports the same back
 * through its hidden echo, while this project's forms hold a real boolean, so
 * the conversion belongs at the field boundary rather than in the listener —
 * the listener is shared with every other control and knows nothing about
 * which kind it is bridging.
 */
const ReskinYesNo = adminField(
  'YesNo',
  ({hint, label, ...props}: any) => {
    const id = useId()
    return (
      <ReskinField
        controlType="radio-pair"
        fieldId={id}
        format={(v: any) => (v ? 'true' : 'false')}
        hint={hint}
        label={label}
        parse={(v: any) => v === 'true'}
        {...props}
      />
    )
  },
  {ownRow: true},
)

/**
 * A boolean field.
 *
 * The option reversal this used to carry is gone (design.md — Decision 7). It
 * made render order depend on which of `yes`/`no` appeared first in a props
 * object literal, and no live call site writes `no` first — the one that does
 * is inside a JSX comment in `components/Browse/admin.tsx`.
 *
 * A pair with its own wording keeps the client-built row: `<t-radio-pair>`
 * renders the words "Yes" and "No" literally and has no attribute for either,
 * so handing one over would silently replace the question it asks. Same shape
 * of limit as `A.Input`'s datalist — design.md — Decision 3.
 */
export const YesNo = (props: any) => {
  const ownWording = props.yes != null || props.no != null
  if (useReskin() && !ownWording) {
    return <ReskinYesNo {...props} />
  }
  const options = [
    {value: true, label: props.yes || 'Yes'},
    {value: false, label: props.no || 'No'},
  ]
  return <Radio options={options} parse={(v: any) => v === 'true'} {...props} />
}

// Biggest hack ever. "change" is stable reference, so use it as a key into
// a WeakMap to save field values for restoration if we have to force them.
// Eventually should add another provider in InnerBlockAdmin to supply this
// storage instead of a WeakMap.
const FORM_STORAGE = new WeakMap()
const useFormStorage = () => {
  const {change: key} = Final.useForm()
  let storage: Record<string, any> | undefined = FORM_STORAGE.get(key)
  if (!storage) {
    storage = {}
    FORM_STORAGE.set(key, storage)
  }
  return storage
}

const useForceCheckboxProps = ({name, force}: any) => {
  const {change, getState} = Final.useForm()
  const value = getIn(getState().values, name)
  const saved = useFormStorage()

  useEffect(() => {
    if (force !== undefined) {
      if (value !== force) {
        log.log(
          `useForceCheckboxProps: forcing ${name} to ${force} (was: ${value})`,
        )
        saved[name] ??= value
        change(name, force)
      }
    } else if (saved[name] !== undefined) {
      if (value !== saved[name]) {
        log.log(
          `useForceCheckboxProps: restoring ${name} to ${saved[name]} (was: ${value})`,
        )
        change(name, saved[name])
      }
      delete saved[name]
    }
  }, [name, value, force, change, saved])
  return force === undefined ? {} : {disabled: true}
}

const Checkbox = ({hint, label, left, name, force, ...props}: any) => {
  const reskin = useReskin()
  const forceProps = useForceCheckboxProps({name, force})
  const field = (
    <Field
      type="checkbox"
      name={name}
      component={reskin ? 'input' : S.Input}
      {...props}
      {...forceProps}
    />
  )
  if (reskin) {
    // `left` is ignored here, and the distinction it draws does not survive
    // the reskin. It put `A.CheckRight`'s label *before* its box, which read
    // correctly in classic because the label sat in a right-aligned gutter
    // with the control to its right. The reskin has no gutter, and every
    // checkbox and radio the admin renders — `.t-checkline`, `.t-checkbox`,
    // `.t-radio`, `checkBoxItem`, `choiceOption` — puts the control first
    // without exception. So both components render the same line here, and
    // the two stay separate only for classic.
    const line = (
      <label className="t-checkline">
        {field}
        {label}
      </label>
    )
    // No call site has a hint, but the classic branch renders one and this
    // must not silently swallow one added later. Separation comes from a
    // stack's gap rather than from a margin on the hint, which is the rule
    // outside a `.t-prose` wrapper.
    return hint ? (
      <div className="t-stack" data-gap="1">
        {line}
        <Hint>{hint}</Hint>
      </div>
    ) : (
      line
    )
  }
  return (
    <div>
      <Label>
        {label && !left && <OptionLabel left>{label}</OptionLabel>}
        {field}
        {label && left && <OptionLabel>{label}</OptionLabel>}
      </Label>
      {!!hint && <Hint>{hint}</Hint>}
    </div>
  )
}

// ownLabel: a checkbox's label sits beside it, not above it. See adminField.
export const CheckLeft = adminField('CheckLeft', Checkbox, {
  colon: false,
  left: true,
  ownLabel: true,
})

export const CheckRight = adminField('CheckRight', Checkbox, {ownLabel: true})

/**
 * If the options change so that the current value is no longer valid, reset it
 * to the first option.
 *
 * Then, if the options change so that the previously-selected value becomes
 * valid, restore it.
 */
const useValidOption = ({
  name,
  noneOption,
  options: _options,
  format = R.identity,
  parse = R.identity,
}: {
  name: string
  noneOption: any
  options: OptionVL[] | undefined
  format: any
  parse: any
}) => {
  const {change, getState} = Final.useForm()
  const value = format(getIn(getState().values, name))
  const saved = useFormStorage()

  const options: OptionVL[] = useMemo(() => {
    let options = _options || []
    if (
      (noneOption || typeof noneOption === 'string') &&
      !options.some(o => o.value === '')
    ) {
      const label = typeof noneOption === 'string' ? noneOption : '(none)'
      options = [{value: '', label}, ...options]
    }
    return options
  }, [_options, noneOption])

  useEffect(() => {
    const [state, setState] = [
      saved[name],
      (v?: any) => {
        log.log({name, v})
        if (v === undefined) {
          delete saved[name]
        } else {
          saved[name] = v
        }
      },
    ] as const
    if (!options.some(o => o.value === value)) {
      const validValue = options[0]?.value
      if (validValue !== undefined) {
        log.log(
          `useValidOption: setting ${name} to valid value: "${validValue}" (was: "${value}")`,
        )
        setState({invalidValue: value, ...state, validValue})
        change(name, parse(validValue))
      }
    } else if (state) {
      if (value !== state.validValue) {
        log.log(`useValidOption: clearing ${name} state`)
        setState()
      } else if (options.some(o => o.value === state.invalidValue)) {
        log.log(
          `useValidOption: restoring ${name} to previous value: "${state.invalidValue}"`,
        )
        change(name, parse(state.invalidValue))
        setState()
      }
    }
  }, [options, change, saved, name, parse, value])

  return {options}
}

export const Select = adminField('Select', (allProps: any) => {
  const {
    hint,
    label,
    noneOption,
    options: _options,
    fluid,
    name,
    parse,
    format,
    ref,
    ...props
  } = allProps

  const reskin = useReskin()

  const {options} = useValidOption({
    name,
    noneOption,
    options: _options,
    format,
    parse,
  })

  const field = (
    <Field
      ref={ref}
      type="select"
      name={name}
      parse={parse}
      format={format}
      {...props}
    >
      {({input}: any) => (
        <select
          {...props}
          {...input}
          // `db` is dropped under the reskin, and it was never what its
          // comment claimed. The rule is `select.db {max-width: 500px}` in
          // `classic/tizra.css` and `admin/legacy.css`, absent from
          // `admin/tizra.css`; no admin script names it — the `db` matches in
          // `tagpicker.v3/v4.js` are `-db-input` id fragments. So it hid
          // nothing from anything (design.md — Decision 7).
          className={reskin ? 't-select' : 'db'}
          style={{
            // `fluid` goes with classic. The admin stretches its text-like
            // controls and deliberately not its selects — a select sizes to
            // its longest option — so the two `fluid` call sites in
            // `HeaderBlock` follow the admin rather than override it. A
            // per-call-site cap like `Browse`'s 180px on "Sort by" is layout
            // and stays.
            ...(fluid && !reskin && {width: '100%'}),
            ...props.style,
          }}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  )

  // Under the reskin `adminField` has already built the row, so the control is
  // all that is left to render; `label` and `hint` never arrive here.
  return reskin ? (
    field
  ) : (
    <div>
      {!!label && <Label>{label}</Label>}
      {field}
      {!!hint && <Hint>{hint}</Hint>}
    </div>
  )
})

const checklistParser = (_v: string) => (vs: string[]) => {
  // Final Form passes array with value added or removed.
  return vs
}

const checklistFormatter = (v: string) => (vs?: string[]) => {
  // Final Form wants an array containing or omitting value.
  // We could pass back vs here, it would be the same thing.
  return vs?.includes(v) ? [v] : []
}

const unchecklistParser = (v: string) => (vs: string[]) => {
  // Final Form passes array with value added or removed.
  // (In fact, in the "added" case, it will be doubled since the incoming list
  // of values is the unchecked list.)
  // For Unchecklist, we need to invert the given value.
  return vs.includes(v) ? vs.filter(vv => vv !== v) : vs.concat(v)
}

const unchecklistFormatter = (v: string) => (vs?: string[]) => {
  // Final Form wants an array containing or omitting value.
  // Invert the given value, so that the presentation is the opposite.
  return vs?.includes(v) ? [] : [v]
}

export const Checklist = adminField(
  'Checklist',
  ({
    hint,
    label,
    labelledBy,
    options,
    parser = checklistParser,
    formatter = checklistFormatter,
    ...props
  }: any) => {
    const reskin = useReskin()
    const boxes = (options as OptionVL[]).map(o => (
      <Field
        key={o.value}
        component={reskin ? 'input' : S.Input}
        type="checkbox"
        value={o.value}
        parse={parser(o.value)}
        format={formatter(o.value)}
        {...props}
      />
    ))
    if (reskin) {
      // The admin's own arrangement, exactly: `checkBoxItem` is a single
      // composable item and "callers own the vertical arrangement (a
      // t-stack)", which its `useCheckboxes` caller renders as
      // `<div class="t-stack" data-gap="2">`.
      //
      // No `role`/`aria-labelledby`, for the same reason — the admin names
      // no checkbox set either. That is a real gap, but it is the admin's:
      // grouping these here and nowhere else would make this project's
      // checklists announce differently from the admin's own, which is the
      // divergence this change exists to remove. `labelledBy` is destructured
      // only to keep it off the DOM.
      return (
        <div className="t-stack" data-gap="2">
          {(options as OptionVL[]).map((o, i) => (
            <label className="t-checkbox" key={o.value}>
              {boxes[i]}
              {o.label}
            </label>
          ))}
        </div>
      )
    }
    return (
      <div>
        {!!label && <Label display="block">{label}</Label>}
        {(options as OptionVL[]).map((o, i) => (
          <Option key={o.value}>
            <Label>
              {boxes[i]}
              <OptionLabel>{o.label}</OptionLabel>
            </Label>
          </Option>
        ))}
        {!!hint && <Hint>{hint}</Hint>}
      </div>
    )
  },
  // Several controls under one label, so the label cannot point at any one
  // of them. See adminField.
  {group: true},
)

/**
 * A checklist whose boxes mean the opposite of the values it stores: the field
 * holds what is *excluded*, and a box is checked when its value is absent.
 * `AttachmentsBlock`'s "Included sources" over `excludedSources`, and
 * `DetailsBlock`'s "Included properties" over `excludes`.
 *
 * The inversion lives entirely in `unchecklistParser`/`unchecklistFormatter`,
 * which are Final Form's view of the field and not part of its markup — which
 * is why the reskin branch above can restructure the markup without touching
 * it. There are tests either side of the branch for that.
 */
export const Unchecklist = (props: ComponentProps<typeof Checklist>) => (
  <Checklist
    parser={unchecklistParser}
    formatter={unchecklistFormatter}
    {...props}
  />
)

export const Textarea = adminField(
  'Textarea',
  ({hint, label, ownRow, ...props}: any) => {
    const id = useId()
    if (useReskin() && ownRow) {
      return (
        <ReskinField
          controlType="textarea"
          fieldId={id}
          hint={hint}
          label={label}
          {...props}
        />
      )
    }
    return (
      <div>
        {!!label && (
          <div>
            <Label htmlFor={id}>{label}</Label>
          </div>
        )}
        <div>
          <Field
            id={id}
            component="textarea"
            rows="8"
            cols="75"
            {...props}
            style={{maxWidth: '100%', ...props.style}}
          />
        </div>
        {!!hint && <Hint>{hint}</Hint>}
      </div>
    )
  },
  {
    // No exception here, unlike `A.Input`: `<t-textarea>` has nothing this
    // shape cannot carry. design.md — Decision 3 named `SearchBlock`'s
    // custom-search-tips field as the one control that had to keep a
    // client-built row, on both the hint and the ref conditions, but that
    // field is in a `GlobalAdmin` and so is out of scope (proposal.md).
    //
    // `rows`, `cols` and the `maxWidth` cap go with the classic branch. They
    // sized a bare `<textarea>`; `.t-textarea` carries its own `min-height`
    // and `resize: vertical`, and its width in a dialog is the admin's to fix
    // (tasks.md — *What this change leaves behind*, A.6).
    ownRow: true,
  },
)

export type {Justification} from 'quickstart/types'

/**
 * Accessible names for the compass grid. The stored values are terse because
 * they are a wire format; a screen reader reading "n w" as a radio's name is
 * not a control anyone can use, and there is no visible text to fall back on
 * — the whole control is nine unlabelled boxes in a square.
 */
const JUSTIFICATION_NAMES: Record<string, string> = {
  left: 'Left',
  center: 'Center',
  right: 'Right',
}

const COMPASS_NAMES: Record<string, string> = {
  nw: 'Top left',
  n: 'Top',
  ne: 'Top right',
  w: 'Left',
  '': 'Center',
  e: 'Right',
  sw: 'Bottom left',
  s: 'Bottom',
  se: 'Bottom right',
}

/**
 * One cell of a justification or compass grid.
 *
 * `<label class="t-radio">` around the input rather than a bare input, which
 * is what it was: that is the admin's own rule for a radio, and it is what
 * sizes the control to 15px and gives it the brand accent. The label carries
 * no text — there is nowhere to put it in a 3×3 square — so the name goes on
 * the input as `aria-label`.
 */
const GridRadio = ({label, name, value}: any) => (
  <label className="t-radio">
    <Field type="radio" name={name} value={value}>
      {({input}: any) => <input aria-label={label} {...input} />}
    </Field>
  </label>
)

/** The reskin's radio is 15px; `--t-space-1` is the admin's tightest gap. */
const gridStyle = (rows: boolean) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 15px)',
  ...(rows && {gridTemplateRows: 'repeat(3, 15px)'}),
  gap: 'var(--t-space-1)',
})

export const JustificationInput = adminField(
  'Justification',
  ({ref, hint, label, labelledBy, name}: any) => {
    if (useReskin()) {
      return (
        <div
          aria-labelledby={labelledBy}
          ref={ref}
          role="radiogroup"
          style={gridStyle(false)}
        >
          {justifications.map(value => (
            <GridRadio
              key={value}
              label={JUSTIFICATION_NAMES[value]}
              name={name}
              value={value}
            />
          ))}
        </div>
      )
    }
    return (
      <div ref={ref} style={{marginBottom: '1rem'}}>
        {!!label && (
          <div>
            <Label>{label}</Label>
          </div>
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 14px)',
            gap: '4px',
          }}
        >
          {justifications.map(value => (
            <Field type="radio" name={name} value={value} key={value}>
              {({input}: any) => <input {...input} />}
            </Field>
          ))}
        </div>
        {!!hint && <Hint>{hint}</Hint>}
      </div>
    )
  },
  // Several controls under one label, so the label cannot point at any one
  // of them. See adminField.
  {group: true},
)

export type {CompassDirection} from 'quickstart/types'

export const CompassInput = adminField(
  'CompassInput',
  ({ref, hint, label, labelledBy, name}: any) => {
    if (useReskin()) {
      return (
        <div
          aria-labelledby={labelledBy}
          ref={ref}
          role="radiogroup"
          style={gridStyle(true)}
        >
          {compassDirections.map(value => (
            <GridRadio
              key={value}
              label={COMPASS_NAMES[value]}
              name={name}
              value={value}
            />
          ))}
        </div>
      )
    }
    return (
      <div ref={ref} style={{marginBottom: '1rem'}}>
        {!!label && <Label display="block">{label}</Label>}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 14px)',
            gridTemplateRows: 'repeat(3, 14px)',
            gap: '4px',
          }}
        >
          {compassDirections.map(value => (
            <Field type="radio" name={name} value={value} key={value}>
              {({input}: any) => <input {...input} />}
            </Field>
          ))}
        </div>
        {!!hint && <Hint>{hint}</Hint>}
      </div>
    )
  },
  // Several controls under one label, so the label cannot point at any one
  // of them. See adminField.
  {group: true},
)

interface NavOutProps {
  tab: 'Design'
  subtab: 'features' | 'nav'
}

export const NavOut: FC<NavOutProps> = ({tab, subtab}) => {
  const context = useAdminContext()
  // TODO UniversalLink?
  return (
    // biome-ignore lint/a11y/useValidAnchor: cough
    <a
      href="#"
      onClick={e => {
        e.preventDefault()
        // Both admins, because the dialog differs between them: classic's is a
        // div.dialogbox, the reskin's a <dialog class="t-dialog">. Matching
        // only the former meant hideDialog was skipped under the reskin while
        // navOut ran anyway -- and navOut replaces the page region, removing
        // the dialog without .close() and so without the `close` event the
        // admin binds our teardown to, leaving the tree mounted on a detached
        // node. We are causing the dismissal here, not detecting one.
        const db = (e.target as HTMLElement).closest('dialog, .dialogbox')
        if (db instanceof HTMLElement) {
          window.hideDialog(db)
        }
        window.navOut(
          `ajax_ObjectView("${tab}", ${context.siteDisplayId}, "${subtab}")`,
          'eval',
        )
      }}
    >
      Presentation / {subtab === 'features' ? 'Features' : 'Nav & Footer'}
    </a>
  )
}

export type {BlockConfig, GlobalBlockConfig} from 'quickstart/types'

/**
 * The element {@link Dropdown} opens from: a single control that both
 * carries the control's appearance and takes focus, rather than an unstyled
 * focusable wrapper around a styled child (tasks.md — 4.2). Also used
 * standalone, disabled, to render an unavailable dropdown as the same control
 * in an unavailable state (tasks.md — 3.2) — `NavTree`'s type switcher, when
 * there is nowhere left to switch to.
 */
const dropdownTrigger = (
  reskin: boolean,
  isString: boolean,
  props: Record<string, unknown> = {},
) =>
  reskin ? (
    <button className="t-button" type="button" {...props} />
  ) : isString ? (
    <S.InputButton {...props} />
  ) : (
    <S.SimpleButton {...props} />
  )

export const Dropdown = ({
  children,
  disabled,
  options,
  onChoose,
}: {
  children: ReactNode
  disabled?: boolean
  options: Array<{value: string; label?: ReactNode}>
  onChoose: (value: string) => void
}) => {
  const reskin = useReskin()
  const isString = typeof children === 'string'
  if (disabled) {
    return dropdownTrigger(reskin, isString, {disabled: true, children})
  }
  return (
    <AK.MenuProvider>
      <AK.MenuButton render={dropdownTrigger(reskin, isString)}>
        {children}
      </AK.MenuButton>
      <AK.Menu render={<S.Menu />}>
        {options.map(({value, label}, i) =>
          value === '---' ? (
            <AK.MenuSeparator
              key={i}
              style={{
                display: 'block',
                border: 'none',
                borderTop: '1px solid black',
              }}
            />
          ) : (
            <AK.MenuItem
              key={i}
              render={<S.MenuItem />}
              onClick={() => onChoose(value)}
            >
              {label || value}
            </AK.MenuItem>
          ),
        )}
      </AK.Menu>
    </AK.MenuProvider>
  )
}

export const Icon = {
  Bookshelf: FiBook,
  Check: FiCheck,
  ChevronDown: FiChevronDown,
  CirclePlus: FiPlusSquare,
  Cpu: FiCpu,
  DragHandle: RxDragHandleDots2,
  Link: FiLink,
  LinkOut: FiExternalLink,
  Menu: FiMenu,
  More: FiMoreVertical,
  Plus: FiPlus,
  Right: FiArrowRight,
  Separator: FiMoreHorizontal,
  Trash: FiTrash,
  User: FiUser,
}

export const containerOptions = {
  default: (
    <span>
      Automatic &mdash;{' '}
      <a
        href="https://baymard.com/blog/line-length-readability"
        target="_blank"
        rel="noreferrer"
      >
        optimal line length of approximately 75 characters.
      </a>
    </span>
  ),
  wider: (
    <span>
      Relaxed &mdash; trades readability for using more of the available
      horizontal space.
    </span>
  ),
  none: (
    <span>
      Full-width &mdash; use with caution, since it may hinder readability.
    </span>
  ),
}

export const justificationOptions = {
  left: 'Left justified',
  center: 'Centered',
  right: 'Right justified',
}
